/**
 * The 2D map's four layouts (D340, docs/plans/2026-10-memory-globe.md): Force, Circle, Hex and Rings, all of them over the
 * hierarchy of the control: a hub per layer, a hub per group (a department of memory, a phase, a connector, a kind of
 * skill…), a bubble per folder of memory with the count of what is inside it, and the things themselves. No DOM.
 *
 * Ported from RUBRIC Agentic OS v2 (Jay E, RoboNuggets, MIT; see THIRD-PARTY-NOTICES.md), brain/_core.js:
 *   - `rebuildStructure` / `expandDir`: the centre routes to a hub per layer, a hub spokes to its members, a directory is a
 *     bubble whose files appear when it is expanded and spoke to it.
 *   - `setLayout` / `buildSim`: Force as clusters round their hubs (a starburst each), Circle and Hex as the same clusters
 *     held inside a wall (`R = max(300, 30 * sqrt(n / pi))`, `maxR`, `hexF`).
 *   - `computeRingTargets` (the Rings layout): skills on the innermost rings, a full circle each, faster spin; the memory
 *     band outside them, one sector per department as wide as the square root of its size, its nodes in arcs of rows, a hub
 *     on a spoke from the centre; then routines, then applications on rings of their own. Ours carries the other five
 *     tiers outward from there, and every ring is grouped: a group is a run of the ring that opens with its hub.
 * The source runs a d3 force simulation. Here a group is a Vogel (sunflower) cluster round its hub, folders are satellites
 * round it, which is already free of overlaps: no simulation, no dependency, a few milliseconds for thousands of nodes.
 */
import { ARMS_THEMES } from './armsGroupingModel.js';
import { type Globe, GLOBE_LAYERS, type GlobeLayer, type GlobeNode, isFoldable } from './memoryGlobeModel.js';
import { layoutInnerRing, type OpenFocus, partitionKids, type RimPartition } from './memoryRimModel.js';

/** Circle was dropped (JP, 25 Oct 2026): it was Hex's packing inside a round wall, the same picture; a saved Circle reads as Hex. */
export type MapLayoutKind = 'force' | 'hex' | 'rings' | 'shelves';
export const MAP_LAYOUTS: ReadonlyArray<{ id: MapLayoutKind; label: string; hint: string }> = [
  { id: 'rings', label: 'Rings', hint: 'Concentric: skills, memory by topic and folder, routines, tools, then roadmap to library outwards.' },
  { id: 'force', label: 'Force', hint: 'Every layer and group as a cluster round its hub, linked to the centre.' },
  { id: 'hex', label: 'Hex', hint: 'The clusters packed tight inside a hexagon.' },
  { id: 'shelves', label: 'Shelves', hint: 'One shelf per tier (or theme) with its things as chips, and every graph node under its topic.' },
];

const TAU = Math.PI * 2;
const SPACING = 6.2;
const clampN = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));

/** A bubble: a node that weighs more is drawn bigger, in layout units; light ones are dots. */
export function bubbleRadius(weight: number): number {
  return weight <= 3 ? 2.8 : Math.min(7, 2.8 + 3.6 * Math.sqrt((weight - 3) / 60));
}

/** How big a node is drawn on the map, in layout units. */
export function mapNodeRadius(layer: GlobeLayer, weight = 1): number {
  if (layer === 'memory') return bubbleRadius(weight);
  if (layer === 'skills') return 3.6;
  if (layer === 'routines') return 4.6;
  return 5.4;
}

/** A node's size on the map: a hub or a folder by what it holds, the rest by their layer. */
export function mapRadiusOf(n: Pick<GlobeNode, 'layer' | 'weight' | 'struct'>): number {
  if (n.struct === 'theme') return 13;
  if (n.struct === 'layer') return 10;
  if (n.struct === 'group') return Math.min(16, 6.5 + 1.5 * Math.sqrt(n.weight));
  if (n.struct === 'dir') return Math.min(13, 5 + 1.4 * Math.sqrt(n.weight));
  return mapNodeRadius(n.layer, n.weight);
}

export interface MapCaption {
  key: string;
  text: string;
  x: number;
  y: number;
  kind: 'layer' | 'department';
  layer?: GlobeLayer;
  deptIndex?: number;
}

/** What turns on the Rings layout: every node's angle and radius round the centre, and how fast its ring spins. */
export interface MapPolar {
  angle: Float32Array;
  radius: Float32Array;
  /** Relative to the picture's spin: skills turn fastest, routines slowly, tools and the outer rings not at all. */
  spin: Float32Array;
}

export interface MapLayout {
  kind: MapLayoutKind;
  /** How much bigger than its base size a node is drawn: a small memory is spread out and its nodes grow with it. */
  unit: number;
  /** Position of node `i` (`Globe.nodes[i]`), layout units, the centre at 0, 0. */
  xs: Float32Array;
  ys: Float32Array;
  /** How far from the centre anything reaches, with its own radius: what the camera fits. */
  extent: number;
  /** Circle and Hex: the wall. */
  bound?: { type: 'circle' | 'hex'; R: number };
  /** Kept for the painter's backdrop; the layers and groups name themselves through their hubs. */
  captions: MapCaption[];
  /** Concentric guides: a faint circle per layer (Rings, Force). */
  guides: Array<{ layer: GlobeLayer; r: number }>;
  /** The nodes the centre is routed to: a hub per layer (Force, Circle, Hex) or per department (Rings). */
  centreLinks: number[];
  polar?: MapPolar;
  /** The rim state (a group is open): how much each node is scaled (the rim's hubs are drawn smaller), the arcs of the wide inner ring and their counted links to the rim. */
  rim?: RimGeometry;
  scale?: Float32Array;
}

/** An arc of the opened ring: where it lies, what it is called and how many it stands for. */
export interface RimArc {
  label: string;
  a0: number;
  a1: number;
  r0: number;
  r1: number;
  count: number;
  layer: GlobeLayer;
  deptIndex: number;
}

/** A counted link from an arc to a hub on the rim: its own group (what is in the arc) or another's (links the graph draws across). */
export interface RimLink {
  arc: number;
  /** The rim hub it runs to (a node index). */
  to: number;
  count: number;
  own: boolean;
}

export interface RimGeometry {
  focus: number;
  chain: number[];
  arcs: RimArc[];
  links: RimLink[];
  /** Where the arcs begin and end, and the rim's first lane. */
  innerR: number;
  outerR: number;
  rimR: number;
}

/** The hexagon's reach in direction `a` (apothem over the cosine): `maxR` in the source. */
export function hexReach(R: number, a: number): number {
  const apothem = R * Math.cos(Math.PI / 6);
  const t = ((a % (Math.PI / 3)) + Math.PI / 3) % (Math.PI / 3);
  return apothem / Math.cos(t - Math.PI / 6);
}

/** The wall's distance from the centre in direction `a`. */
export function boundReach(bound: NonNullable<MapLayout['bound']>, a: number): number {
  return bound.type === 'circle' ? bound.R : hexReach(bound.R, a);
}

/** A small memory gets room: nodes and gaps grow as the count falls, from 1 at about 700 nodes to 3 for a few dozen. */
export function unitOf(globe: Globe): number {
  const real = GLOBE_LAYERS.reduce((s, k) => s + globe.counts[k], 0);
  const n = globe.counts.memory + 0.5 * (real - globe.counts.memory);
  return clampN(Math.sqrt(900 / Math.max(1, n)), 1, 3.2);
}

/** What the layouts are given: which nodes a closed folder hides (1 for hidden). */
export type FoldMask = Uint8Array | undefined;

interface Work {
  globe: Globe;
  hid: FoldMask;
  u: number;
  rad: Float32Array;
  xs: Float32Array;
  ys: Float32Array;
  /** The radius of the cluster each hub or folder heads, memoised. */
  reach: Float32Array;
}

const shown = (w: Work, i: number) => !w.hid?.[i];

function work(globe: Globe, hid: FoldMask): Work {
  const n = globe.nodes.length;
  const u = unitOf(globe);
  const rad = new Float32Array(n);
  for (const node of globe.nodes) rad[node.index] = mapRadiusOf(node) * u;
  return { globe, hid, u, rad, xs: new Float32Array(n), ys: new Float32Array(n), reach: new Float32Array(n).fill(-1) };
}

/** What hangs from a hub or folder and is on show: its folders, then its things. */
function kidsOf(w: Work, i: number): { dirs: number[]; leaves: number[] } {
  const dirs: number[] = [];
  const leaves: number[] = [];
  for (const c of w.globe.nodes[i].children) {
    if (!shown(w, c)) continue;
    if (w.globe.nodes[c].struct === 'dir') dirs.push(c);
    else leaves.push(c);
  }
  return { dirs, leaves };
}

function vogelAt(k: number, spacing: number, k0: number): { x: number; y: number } {
  const r = spacing * Math.sqrt(k + k0 + 0.5);
  const a = k * 2.399963229728653;
  return { x: r * Math.cos(a), y: r * Math.sin(a) };
}

/** How far the cluster headed by node `i` reaches from its centre: the hub, its things in a starburst, its folders as satellites. */
function reachOf(w: Work, i: number): number {
  if (w.reach[i] >= 0) return w.reach[i];
  const base = w.rad[i];
  const { dirs, leaves } = kidsOf(w, i);
  const sp = SPACING * w.u;
  const k0 = Math.max(0, ((base + 7 * w.u) / sp) ** 2 - 0.5);
  const leafR = leaves.length ? sp * Math.sqrt(leaves.length + k0 + 0.5) + 4 * w.u : base;
  const core = Math.max(base, leafR);
  let r = core;
  if (dirs.length) {
    const subs = dirs.map((d) => reachOf(w, d));
    const biggest = Math.max(...subs);
    const orbit = Math.max(core + biggest + 6 * w.u, dirs.length > 1 ? ((biggest + 3 * w.u) / Math.sin(Math.PI / dirs.length)) : 0);
    r = orbit + biggest;
  }
  w.reach[i] = r;
  return r;
}

/** Put the cluster headed by node `i` with its centre at (cx, cy). */
function placeCluster(w: Work, i: number, cx: number, cy: number): void {
  w.xs[i] = cx;
  w.ys[i] = cy;
  const { dirs, leaves } = kidsOf(w, i);
  const base = w.rad[i];
  const sp = SPACING * w.u;
  const k0 = Math.max(0, ((base + 7 * w.u) / sp) ** 2 - 0.5);
  leaves.forEach((l, k) => {
    const p = vogelAt(k, sp, k0);
    w.xs[l] = cx + p.x;
    w.ys[l] = cy + p.y;
  });
  if (!dirs.length) return;
  const leafR = leaves.length ? sp * Math.sqrt(leaves.length + k0 + 0.5) + 4 * w.u : base;
  const core = Math.max(base, leafR);
  const subs = dirs.map((d) => reachOf(w, d));
  const biggest = Math.max(...subs);
  const orbit = Math.max(core + biggest + 6 * w.u, dirs.length > 1 ? (biggest + 3 * w.u) / Math.sin(Math.PI / dirs.length) : 0);
  dirs.forEach((d, j) => {
    const a = -Math.PI / 2 + ((j + 0.5) / dirs.length) * TAU;
    placeCluster(w, d, cx + Math.cos(a) * orbit, cy + Math.sin(a) * orbit);
  });
}

/** The things a layer is made of, in the order they are laid out: its group hubs, the biggest first. */
function groupsOf(globe: Globe, layer: GlobeLayer): number[] {
  const hub = globe.layerHubs[layer];
  return hub === undefined ? [] : globe.nodes[hub].children.filter((c) => globe.nodes[c].struct === 'group');
}

/** The layers from the centre outwards. */
const INWARD: readonly GlobeLayer[] = [...GLOBE_LAYERS].reverse();

/** How much further out a ring sits for what it holds: proportional to the square root of its items, nothing below a floor of a dozen. */
export function populationReach(items: number, u: number, k: number): number {
  return k * u * Math.sqrt(Math.max(12, items) - 12);
}

/** One ring of a grouping: what the centre is routed to, the colour of its guide, and its units in order (hubs, then groups). */
interface RingOf {
  top: number;
  guide: GlobeLayer;
  /** The hubs of the tiers it holds, then the groups of each, in order. */
  units: number[];
}

/**
 * The rings of a grouping from the centre out: one per layer under nine tiers; one per theme under five, holding the
 * theme's hub, then each of its tiers' hub and groups. Everything under a hub is a cluster headed by a group.
 */
function ringsOf(globe: Globe): RingOf[] {
  if (globe.grouping === 'tiers') {
    const out: RingOf[] = [];
    for (const layer of INWARD) {
      const lh = globe.layerHubs[layer];
      if (lh !== undefined) out.push({ top: lh, guide: layer, units: [lh, ...groupsOf(globe, layer)] });
    }
    return out;
  }
  const out: RingOf[] = [];
  for (const theme of [...ARMS_THEMES].reverse()) {
    const th = globe.themeHubs[theme.id];
    if (th === undefined) continue;
    const units = [th];
    for (const tier of theme.tiers) {
      const lh = globe.layerHubs[tier];
      if (lh !== undefined) units.push(lh, ...groupsOf(globe, tier));
    }
    out.push({ top: th, guide: theme.tiers[0], units });
  }
  return out;
}

/** A hub that is drawn as one node, not a cluster: a theme's or a layer's. */
const isHubOnly = (globe: Globe, i: number) => globe.nodes[i].struct === 'layer' || globe.nodes[i].struct === 'theme';

function extentOf(w: Work, floor = 200): number {
  let e = floor;
  for (const n of w.globe.nodes) if (shown(w, n.index)) e = Math.max(e, Math.hypot(w.xs[n.index], w.ys[n.index]) + w.rad[n.index]);
  return e;
}

/** Hide-aware cleanup: nodes a closed folder hides are tucked on their folder, so a glide ends where they will return from. */
function tuckHidden(w: Work): void {
  const { nodes } = w.globe;
  for (let i = 0; i < nodes.length; i++) {
    if (!shown(w, i)) {
      let p = nodes[i].parent;
      while (p >= 0 && !shown(w, p)) p = nodes[p].parent;
      if (p >= 0) {
        w.xs[i] = w.xs[p];
        w.ys[i] = w.ys[p];
      }
    }
  }
}

/** Force: the centre routed to a hub per layer, each layer a ring of clusters, each cluster a hub with its things in a starburst. */
function layoutForce(w: Work): MapLayout {
  const { globe } = w;
  const guides: MapLayout['guides'] = [];
  const centreLinks: number[] = [];
  let edge = 56 * w.u;
  let turn = 0;
  for (const ring of ringsOf(globe)) {
    const units = ring.units.map((i) => ({ i, r: isHubOnly(globe, i) ? w.rad[i] + 4 * w.u : reachOf(w, i) }));
    const need = units.reduce((s, x) => s + 2 * x.r + 20 * w.u, 0);
    const biggest = units.reduce((m, x) => Math.max(m, x.r), 0);
    const radius = Math.max(edge + biggest + 26 * w.u, need / TAU, units.length > 1 ? biggest * 1.25 : 0);
    let a = -Math.PI / 2 + turn;
    for (const x of units) {
      const width = ((2 * x.r + 20 * w.u) / need) * TAU;
      const mid = a + width / 2;
      if (isHubOnly(globe, x.i)) {
        w.xs[x.i] = Math.cos(mid) * radius;
        w.ys[x.i] = Math.sin(mid) * radius;
      } else placeCluster(w, x.i, Math.cos(mid) * radius, Math.sin(mid) * radius);
      a += width;
    }
    turn += 0.9;
    centreLinks.push(ring.top);
    guides.push({ layer: ring.guide, r: radius });
    edge = radius + biggest;
  }
  tuckHidden(w);
  return { kind: 'force', unit: w.u, xs: w.xs, ys: w.ys, extent: extentOf(w), captions: [], guides, centreLinks };
}

/** Circle and Hex: the same clusters, packed as close to the centre as they will go, inside a wall that holds them all. */
function layoutBounded(w: Work, kind: 'hex'): MapLayout {
  const { globe } = w;
  const units: Array<{ i: number; r: number }> = [];
  const centreLinks: number[] = [];
  for (const ring of ringsOf(globe)) {
    centreLinks.push(ring.top);
    for (const i of ring.units) units.push({ i, r: isHubOnly(globe, i) ? w.rad[i] + 8 * w.u : reachOf(w, i) + 6 * w.u });
  }
  const placed: Array<{ x: number; y: number; r: number }> = [{ x: 0, y: 0, r: 46 * w.u }];
  const gap = 20 * w.u;
  for (const unit of units) {
    let found: { x: number; y: number } | undefined;
    for (let rho = 0; !found; rho += 5 * w.u) {
      const steps = Math.max(8, Math.ceil((TAU * Math.max(rho, 1)) / (unit.r * 0.6 + 4)));
      for (let s = 0; s < steps && !found; s++) {
        const a = (s / steps) * TAU + rho * 0.37;
        const x = Math.cos(a) * rho;
        const y = Math.sin(a) * rho;
        if (placed.every((p) => Math.hypot(p.x - x, p.y - y) >= p.r + unit.r + gap)) found = { x, y };
      }
    }
    placed.push({ ...found, r: unit.r });
    if (isHubOnly(globe, unit.i)) {
      w.xs[unit.i] = found.x;
      w.ys[unit.i] = found.y;
    } else placeCluster(w, unit.i, found.x, found.y);
  }
  // The wall is as small as holds every cluster, with a margin.
  let apothem = 100;
  for (const p of placed.slice(1)) {
    for (let k = 0; k < 6; k++) {
      const na = Math.PI / 6 + (k * Math.PI) / 3;
      apothem = Math.max(apothem, Math.cos(na) * p.x + Math.sin(na) * p.y + p.r);
    }
  }
  const R = (apothem + 16 * w.u) / Math.cos(Math.PI / 6);
  tuckHidden(w);
  return { kind, unit: w.u, xs: w.xs, ys: w.ys, extent: Math.max(R, extentOf(w)), bound: { type: kind, R }, captions: [], guides: [], centreLinks };
}

/** A node and what is on show under it, depth first: folders first, then the things. */
function walk(w: Work, i: number, out: number[] = []): number[] {
  out.push(i);
  const { dirs, leaves } = kidsOf(w, i);
  for (const d of dirs) walk(w, d, out);
  for (const l of leaves) out.push(l);
  return out;
}

/** Evenly round a ring, from the top, in as many rows as the count needs; returns the outermost row's radius. */
function ringRows(list: readonly number[], r0: number, pitch: number, rowGap: number, w: Work, polar: MapPolar, spin: number, phase = 0): number {
  let r = r0;
  let at = 0;
  while (at < list.length) {
    const cap = Math.max(6, Math.floor((TAU * r) / pitch));
    const count = Math.min(cap, list.length - at);
    for (let j = 0; j < count; j++) {
      const idx = list[at + j];
      const a = -Math.PI / 2 + phase + ((j + (at % 2 ? 0.5 : 0)) / count) * TAU;
      w.xs[idx] = Math.cos(a) * r;
      w.ys[idx] = Math.sin(a) * r;
      polar.angle[idx] = a;
      polar.radius[idx] = r;
      polar.spin[idx] = spin;
    }
    at += count;
    if (at < list.length) r += rowGap;
  }
  return r;
}

/**
 * Rings, after the source's `computeRingTargets`: skills in the innermost rings, the memory band outside them with one
 * sector per department (width by the square root of its size), its folders and files in arcs of rows, a hub for each
 * department on a spoke from the centre, then routines, tools and our five outer layers. Every ring is grouped: a group
 * is a run of the ring that opens with its hub.
 */
function layoutRings(w: Work): MapLayout {
  const { globe } = w;
  const n = globe.nodes.length;
  const polar: MapPolar = { angle: new Float32Array(n), radius: new Float32Array(n), spin: new Float32Array(n) };
  const u = w.u;
  const pitch = 15 * u;
  const rowGap = 19 * u;
  const guides: MapLayout['guides'] = [];
  const centreLinks: number[] = [];
  const setPolar = (i: number, spin: number) => {
    polar.angle[i] = Math.atan2(w.ys[i], w.xs[i]);
    polar.radius[i] = Math.hypot(w.xs[i], w.ys[i]);
    polar.spin[i] = spin;
  };
  /** A layer's ring as a list: its hub, then each group's run. */
  /** The groups of a big tier that are open: their things are not in the ring but open as a cluster beside their hub. */
  const opened: number[] = [];
  const runOf = (layer: GlobeLayer): number[] => {
    const lh = globe.layerHubs[layer];
    if (lh === undefined) return [];
    return [
      lh,
      ...groupsOf(globe, layer).flatMap((g) => {
        if (!isFoldable(globe, globe.nodes[g])) return walk(w, g);
        // A group of a tier over the budget is one hub on the ring; opened, its things open outwards from it.
        if (kidsOf(w, g).dirs.length + kidsOf(w, g).leaves.length) opened.push(g);
        return [g];
      }),
    ];
  };

  const themes = globe.grouping === 'themes';
  // Skills: innermost, a full circle per row, turning faster. (Under five themes they ride the agent's kit ring.)
  const skills = themes ? [] : runOf('skills');
  let skillsEnd = themes ? 30 * u : 52 * u;
  if (skills.length) {
    skillsEnd = ringRows(skills, 64 * u, pitch, 17 * u, w, polar, 1.6);
    guides.push({ layer: 'skills', r: 64 * u });
  }

  // Memory: a sector per department, its folders opening into rows.
  const depts = groupsOf(globe, 'memory');
  // The band's radius follows its population (the square root of its items, with a floor) and has room for a hub per topic,
  // so memory with 400 nodes is not the smallest ring.
  const hubR = skillsEnd + 42 * u + populationReach(globe.counts.memory, u, 6) + Math.max(0, (depts.length * 30 * u) / TAU - 60 * u);
  const memR = hubR + 24 * u;
  const weights = depts.map((d) => Math.sqrt(Math.max(4, globe.nodes[d].weight)));
  const wSum = weights.reduce((a, b) => a + b, 0) || 1;
  let acc = -Math.PI / 2;
  let maxMemR = memR;
  depts.forEach((d, di) => {
    const width = (weights[di] / wSum) * TAU;
    const mid = acc + width / 2;
    acc += width;
    const half = Math.max(0.02, Math.min(width * 0.42, width / 2 - 0.03)) * 1.05;
    w.xs[d] = Math.cos(mid) * hubR;
    w.ys[d] = Math.sin(mid) * hubR;
    setPolar(d, 1);
    centreLinks.push(d);
    const pool = walk(w, d).slice(1);
    let r = memR;
    let row: number[] = [];
    const flush = () => {
      row.forEach((idx, j) => {
        const frac = row.length === 1 ? 0.5 : j / (row.length - 1);
        const a = mid - half + frac * 2 * half;
        w.xs[idx] = Math.cos(a) * r;
        w.ys[idx] = Math.sin(a) * r;
        polar.angle[idx] = a;
        polar.radius[idx] = r;
        polar.spin[idx] = 1;
      });
      row = [];
      r += rowGap;
    };
    const capAt = () => Math.max(3, Math.floor((2 * half * r) / pitch));
    for (const idx of pool) {
      // A folder opens a row of its own: its bubble leads, what is in it follows.
      if (globe.nodes[idx].struct === 'dir' && row.length) flush();
      row.push(idx);
      if (row.length >= capAt()) flush();
    }
    if (row.length) flush();
    maxMemR = Math.max(maxMemR, r - rowGap);
  });
  const mh = globe.layerHubs.memory;
  if (mh !== undefined) {
    w.xs[mh] = 0;
    w.ys[mh] = -(skillsEnd + 22 * u);
    setPolar(mh, 0);
    guides.push({ layer: 'memory', r: memR - 8 * u });
  }
  const mth = globe.themeHubs.memory;
  if (mth !== undefined) {
    w.xs[mth] = 40 * u;
    w.ys[mth] = -(skillsEnd + 22 * u);
    setPolar(mth, 0);
  }

  // Routines, applications, then the outer layers (library first, roadmap last): one ring each, more rows when it holds many.
  // Under five themes: the agent's kit, then structure, governance and direction, a ring each, their tiers as runs on it.
  let edge = maxMemR;
  let first = true;
  const rings: Array<{ guide: GlobeLayer; list: number[]; spin: number }> = themes
    ? [...ARMS_THEMES]
        .reverse()
        .filter((t) => t.id !== 'memory' && globe.themeHubs[t.id] !== undefined)
        .map((t) => ({ guide: t.tiers[0], list: [globe.themeHubs[t.id]!, ...t.tiers.flatMap((k) => runOf(k))], spin: t.id === 'kit' ? 0.7 : 0 }))
    : (['routines', 'apps', ...[...GLOBE_LAYERS].filter((k) => !['routines', 'apps', 'memory', 'skills'].includes(k)).reverse()] as GlobeLayer[]).map((k) => ({ guide: k, list: runOf(k), spin: k === 'routines' ? 0.7 : 0 }));
  let turn = 0;
  for (const ring of rings) {
    if (!ring.list.length) continue;
    const r0 = Math.max(edge + (first ? 40 : 38) * u + populationReach(themes ? ring.list.length : globe.counts[ring.guide], u, 3.2), (ring.list.length * 17 * u) / TAU);
    first = false;
    // Each ring starts a little further round, so the hubs and their names are never stacked at twelve o'clock.
    turn += 0.62;
    const last = ringRows(ring.list, r0, 17 * u, 19 * u, w, polar, ring.spin, turn);
    guides.push({ layer: ring.guide, r: r0 });
    edge = last;
    // Opened groups: their things fill a wide cluster just outside the ring, the hub staying where it is on it.
    for (const g of opened.filter((x) => ring.list.includes(x))) {
      const hx = w.xs[g];
      const hy = w.ys[g];
      const len = Math.hypot(hx, hy) || 1;
      const reach = reachOf(w, g);
      placeCluster(w, g, hx + (hx / len) * reach, hy + (hy / len) * reach);
      w.xs[g] = hx;
      w.ys[g] = hy;
      for (const i of walk(w, g).slice(1)) setPolar(i, ring.spin);
      edge = Math.max(edge, Math.hypot(hx, hy) + 2 * reach);
    }
  }
  tuckHidden(w);
  for (const i of globe.nodes.keys()) if (!shown(w, i)) setPolar(i, polar.spin[i]);
  return { kind: 'rings', unit: u, xs: w.xs, ys: w.ys, extent: extentOf(w), captions: [], guides, centreLinks, polar };
}


/** Every descendant of node `i`, ignoring what is folded away: the things an arc stands for. */
function under(globe: Globe, i: number, out: number[] = []): number[] {
  for (const c of globe.nodes[i].children) {
    out.push(c);
    under(globe, c, out);
  }
  return out;
}

/**
 * Rings with a level open (D161): the lanes compress into a rim of hubs outside, the opened level's children fill a wide ring
 * inside it in partition arcs (a phase's items by status, a department's folders by count, a folder's files by kind), and each
 * arc is linked, with a count, to the rim hub it belongs to and to the hubs the graph links it across to. Nothing turns.
 */
function layoutRim(w: Work, focus: OpenFocus, links: ReadonlyArray<readonly [number, number]>): MapLayout {
  const { globe } = w;
  const n = globe.nodes.length;
  const u = w.u;
  const polar: MapPolar = { angle: new Float32Array(n), radius: new Float32Array(n), spin: new Float32Array(n) };
  const scale = new Float32Array(n).fill(1);
  const setPolar = (i: number) => {
    polar.angle[i] = Math.atan2(w.ys[i], w.xs[i]);
    polar.radius[i] = Math.hypot(w.xs[i], w.ys[i]);
  };
  const { dirs, leaves } = kidsOf(w, focus.node);
  const parts: RimPartition[] = partitionKids(globe, dirs, leaves);
  const inner = layoutInnerRing(parts, { pitch: 15 * u, rowGap: 19 * u, r0Min: 150 * u, r0Max: 420 * u, step: 8 * u });
  const place = (i: number, a: number, r: number) => {
    w.xs[i] = Math.cos(a) * r;
    w.ys[i] = Math.sin(a) * r;
    polar.angle[i] = a;
    polar.radius[i] = r;
  };
  for (const p of inner.places) place(p.index, p.angle, p.radius);
  // A folder that is the opened level sits at the head of its ring, inside it.
  const head = globe.nodes[focus.node];
  if (head.struct === 'dir') place(focus.node, -Math.PI / 2, Math.max(36 * u, inner.r0 - 44 * u));
  const layer = head.layer;
  const arcs: RimArc[] = inner.arcs.map((a) => ({ label: a.label, a0: a.a0, a1: a.a1, r0: inner.r0, r1: inner.r1, count: a.count, layer, deptIndex: head.deptIndex }));

  // The rim: every lane's hubs on a thin ring of their own, drawn smaller, the lanes in the rings' order outwards.
  const guides: MapLayout['guides'] = [{ layer, r: inner.r0 - 14 * u }];
  const rimStart = inner.r1 + 46 * u;
  let r = rimStart;
  let turn = 0;
  const unitSet = new Set<number>();
  for (const ring of ringsOf(globe)) {
    const list = ring.units.filter((i) => shown(w, i));
    if (!list.length) continue;
    for (const i of list) {
      unitSet.add(i);
      scale[i] = 0.62;
    }
    turn += 0.62;
    const last = ringRows(list, r, 13 * u, 13 * u, w, polar, 0, turn);
    guides.push({ layer: ring.guide, r });
    r = last + 15 * u;
  }
  const outerR = r - 15 * u;

  // Counted links: from each arc to the hub of the group it is in, and across the graph's links to other hubs on the rim.
  const hubOf = (i: number): number => {
    let p = i;
    while (p >= 0 && !unitSet.has(p)) p = globe.nodes[p].parent;
    return p;
  };
  const own = focus.chain[0];
  const rimLinks: RimLink[] = [];
  const arcOfNode = new Map<number, number>();
  inner.arcs.forEach((a, ai) => {
    for (const i of a.nodes) {
      arcOfNode.set(i, ai);
      if (globe.nodes[i].struct === 'dir') for (const d of under(globe, i)) arcOfNode.set(d, ai);
    }
  });
  const across = new Map<string, number>();
  for (const [a, b] of links) {
    for (const [x, y] of [[a, b], [b, a]] as const) {
      const ai = arcOfNode.get(x);
      if (ai === undefined) continue;
      const hub = hubOf(y);
      if (hub < 0 || hub === own || arcOfNode.has(y)) continue;
      const key = `${ai}:${hub}`;
      across.set(key, (across.get(key) ?? 0) + 1);
    }
  }
  inner.arcs.forEach((a, ai) => {
    rimLinks.push({ arc: ai, to: own, count: a.count, own: true });
    const mine = [...across.entries()].filter(([k]) => Number(k.split(':')[0]) === ai).sort((x, y) => y[1] - x[1]).slice(0, 2);
    for (const [k, count] of mine) rimLinks.push({ arc: ai, to: Number(k.split(':')[1]), count, own: false });
  });

  tuckHidden(w);
  for (const i of globe.nodes.keys()) if (!shown(w, i)) setPolar(i);
  const rim: RimGeometry = { focus: focus.node, chain: focus.chain, arcs, links: rimLinks, innerR: inner.r0, outerR: inner.r1, rimR: rimStart };
  return { kind: 'rings', unit: u, xs: w.xs, ys: w.ys, extent: outerR + 24 * u, captions: [], guides, centreLinks: [], polar, scale, rim };
}

/** Where a node of the Rings layout is when the picture has turned by `rot` radians: the polar coordinates plus its ring's spin. */
export function ringsPlace(layout: MapLayout, i: number, rot: number): { x: number; y: number } {
  const p = layout.polar;
  if (!p) return { x: layout.xs[i], y: layout.ys[i] };
  const a = p.angle[i] + rot * p.spin[i];
  return { x: Math.cos(a) * p.radius[i], y: Math.sin(a) * p.radius[i] };
}

/**
 * Lay the globe's nodes out on the plane. `hidden` marks what a closed folder hides. `links` (the graph's own, as node
 * index pairs) feed the rim state's counted links; `focus` is the level that is open, if any (Rings only).
 */
export function layoutMap(globe: Globe, kind: MapLayoutKind, links: ReadonlyArray<readonly [number, number]> = [], hidden?: Uint8Array, focus?: OpenFocus | null): MapLayout {
  const w = work(globe, hidden);
  if (kind === 'rings' && focus) return layoutRim(w, focus, links);
  if (kind === 'rings' || kind === 'shelves') return layoutRings(w);
  if (kind === 'force') return layoutForce(w);
  return layoutBounded(w, kind);
}

/** The camera that fits a layout in a stage: scale and the centre's place. */
export function fitMap(extent: number, width: number, height: number, margin = 60): { k: number; x: number; y: number } {
  return { k: clampN(Math.min(width, height) / (2 * extent + margin * 2), 0.02, 8), x: width / 2, y: height / 2 };
}
